import React from 'react';
import { createRoot } from 'react-dom/client';
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';

import './styles/tailwind-base.css';
// The shared page library's stylesheet. tsup extracts PageRenderer's SCSS
// import into dist/index.css and the JS never pulls it in, so the consumer
// must. The editor (.com) always did; the player never did — which meant
// the Reveal/Layout page animations and, critically, the WebXR
// `html.xr-presenting :has(.ar-canvas)` background strip (the AR
// black-screen fix, #76) were never in the production player bundle.
// scripts/checkLibStyles.mjs fails the build if this goes missing again.
import 'magical-trails-react/dist/index.css';
import './styles/index.scss';
import { recordNfcScanFromLocation } from 'magical-trails-react';
import App from './App';
import { getAnalyticsOptedOut } from './utils/localStorage';
import { installCaughtErrorForwarding } from './monitoring';
import { installBreadcrumbCollectors } from './observability/breadcrumbs';
import { startFreezeWatchdog } from './observability/freezeWatchdog';
import { installOutboxFlush } from './observability/outbox';
import { initServiceWorker } from './registerServiceWorker';

// The shared library's TrailSLAM session recorder stamps recordings with the
// running build; `__APP_BUILD_ID__` is a Vite define the library cannot see,
// so hand it over as a global before anything AR mounts.
(globalThis as { __MT_BUILD_ID__?: string }).__MT_BUILD_ID__ = __APP_BUILD_ID__;

// Owns Service Worker registration for the whole app; called exactly once,
// here. Production only: the dev server serves no sw.js (vite.config.ts
// keeps devOptions disabled so map/AR iteration never fights a stale cache).
if (import.meta.env.PROD) initServiceWorker();

const domNode = document.getElementById('root');
const root = domNode && createRoot(domNode);

// A phone that cannot read NFC from inside a web page (iPhone, and any
// Android browser without Web NFC) instead opens the tag's URL from an OS
// banner: the story's join URL plus `?nfc=<token>`. That landing can hit any
// route — the poster join code, a saved trail, the catch-all — so the token
// is recorded here, once, BEFORE the router renders. The NFC Tap page then
// confirms itself from the record, in this tab or in the one the visitor was
// already reading (iOS opens a new one).
//
// Ahead of posthog.init on purpose: this call strips the token back out of
// the address bar, so analytics never has the chance to see a poster secret
// in a pageview URL. Relying on the SDK to defer its first pageview would
// make that a matter of timing.
try {
  if (typeof window !== 'undefined') recordNfcScanFromLocation();
} catch {
  // Never let a tag landing stop the player from booting.
}

// Cookieless, anonymous, AGGREGATE-only analytics (child-facing product: no
// blocking opt-in gate, no persistent identifier).
//
// Initialised SYNCHRONOUSLY, before the React render below — NOT via
// PostHogProvider's apiKey prop, which initialises in a useEffect after the
// first commit. A returning player's saved trail resolves synchronously
// during that first render (Trail effect → getCode → loadSavedTrail →
// trailProgress → posthog.capture), and posthog-js silently DROPS captures
// that arrive before init — so the provider-driven init lost the load event
// for every returning player. The provider receives this ready client via
// the `client` prop and performs no init of its own.
//
//  - `api_host` — the Vercel env var should point at the same-origin
//    first-party proxy `/ingest` (see vercel.json's rewrites), which
//    sidesteps ad-blockers; the fallback assumes it. `ui_host` tells the
//    SDK where the PostHog app itself lives since api_host no longer says.
//  - `defaults: '2026-05-30'` — pin the SDK's dated defaults preset
//    (PostHog's recommended install shape). Every privacy-critical knob is
//    still set EXPLICITLY below so a future preset can't silently flip one.
//  - `persistence: 'memory'` — posthog-js keeps its distinct_id and session
//    state in JS memory ONLY: no cookie, no localStorage, no IndexedDB.
//    Nothing survives a reload or a new tab; every session gets a fresh,
//    randomly-generated id that cannot be linked back to a prior visit.
//  - `person_profiles: 'never'` — PostHog never creates a Person for these
//    events, so there is no profile to identify, merge, or track across
//    sessions. Trail-progress events (loadTrail/storyPointCompleted/
//    request-trail/etc, see ANALYTICS_EVENTS) still carry a `distinct_id`
//    column on the raw event — that's what the maker dashboard's HogQL
//    reads (`uniq(distinct_id)`) to count aggregate "plays"/"sessions"; it
//    never joins to a `persons` table, so `person_profiles: 'never'`
//    doesn't break it.
//  - `capture_pageview: 'history_change'` — SPA pageviews (still anonymous
//    and session-scoped under the memory persistence above). These feed
//    PostHog's Web Analytics view and its installation-health check, both
//    of which read $pageview.
//  - `autocapture: false` / `disable_session_recording: true` — only
//    pageviews and the explicit, hand-picked trail-progress events are ever
//    captured; no passive click/DOM capture, no session replay.
//  - `opt_out_capturing_by_default` mirrors whatever the player last chose
//    (see utils/localStorage.getAnalyticsOptedOut): missing/invalid ⇒ stays
//    OFF-by-default is false, i.e. analytics stays ON (the cookieless
//    default — there's no persistent identifier for a gate to protect); an
//    explicit opted-out record, or one from an old (pre-redesign) version,
//    starts the session opted out until the player re-confirms.
//  - `capture_exceptions: true` — this PWA's error-monitoring path in lieu
//    of Sentry (Sentry's default tracing/replay instrumentation conflicted
//    with our Service Worker). It turns on posthog-js's Error Tracking
//    autocapture: uncaught exceptions and unhandled promise rejections are
//    reported as `$exception` events, same as any other capture call — so
//    they honour the same opt-out gate as everything else. An opted-out
//    player sends no error events, same as they send no other events.
const posthogKey = import.meta.env.VITE_PUBLIC_POSTHOG_KEY;
if (posthogKey) {
  posthog.init(posthogKey, {
    api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST || '/ingest',
    ui_host: 'https://eu.posthog.com',
    defaults: '2026-05-30',
    persistence: 'memory',
    autocapture: false,
    capture_pageview: 'history_change',
    capture_exceptions: true,
    disable_session_recording: true,
    person_profiles: 'never',
    opt_out_capturing_by_default: getAnalyticsOptedOut(),
  });
}
// Forward library-caught failures (boundary crashes, AR degrades, lost WebGL
// contexts) into PostHog — see src/monitoring.ts. Installed regardless of the
// init above so dev builds still exercise the listener path (captures are
// gated by posthog itself).
installCaughtErrorForwarding();
// Field signals (see src/observability/): breadcrumbs give every report its
// context; the watchdog reports a previous session whose main thread stopped
// while visible (the failure class exception tracking cannot see); the
// outbox replays events captured in a dead zone. Breadcrumbs first so the
// boot crumb precedes everything; watchdog before the flush so a queued
// freeze report from THIS boot rides the same flush.
installBreadcrumbCollectors();
startFreezeWatchdog();
installOutboxFlush();

root?.render(
  <React.StrictMode>
    <PostHogProvider client={posthog}>
      <App />
    </PostHogProvider>
  </React.StrictMode>
);
